import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { ScoreChip } from '../components/common/ScoreChip';
import { EmptyState } from '../components/common/EmptyState';
import { usePlatform } from '../contexts/PlatformContext';
import { MEGA_MAJOR_STAGES, STAGE_META, summariseGate } from '../utils/stages';
import { entityName } from '../data/entities';
import { daysUntil } from '../utils/format';
import { cn } from '../utils/cn';
export function PortfolioBoard() {
  const {
    applications
  } = usePlatform();
  const records = applications.filter(record => record.track !== 'international' && record.status !== 'rejected');
  const stalled = records.filter(record => record.daysInStage > 100);
  const heldGates = records.filter(record => {
    const summary = summariseGate(record.gates[record.stage] ?? []);
    return summary.total > 0 && !summary.cleared;
  });
  return <div data-id="element-1120" data-mp="3uox0drf10sjo">
      <PageHeader eyebrow="Lifecycle" title="Mega & Major portfolio board" description="Every Mega and Major record grouped by its stage in the six-stage governed lifecycle." data-id="element-1121" data-mp="3up6hsqm09rdr" />

      <div className="space-y-4 p-6" data-id="element-1122" data-mp="7imjlwjsqx7b">
        <section className="rounded-sm bg-uae-green p-4 text-white" data-id="element-1123" data-mp="7hkvaqdrse7n">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60" data-id="element-1124" data-mp="cb79imzl0q5k">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1125" data-mp="h98vpgsb15l9" />
            AI portfolio note
          </p>
          <p className="mt-2 max-w-4xl text-[13px] leading-relaxed text-white/85" data-id="element-1126" data-mp="maoe1133kxzy">
            {heldGates.length} of {records.length} records are holding at their current gate, and{' '}
            {stalled.length} have been in stage for more than 100 days. The bottleneck sits at{' '}
            {STAGE_META[MEGA_MAJOR_STAGES.reduce((worst, stage) => {
            const count = records.filter(r => r.stage === stage).length;
            const worstCount = records.filter(r => r.stage === worst).length;
            return count > worstCount ? stage : worst;
          }, MEGA_MAJOR_STAGES[0])].label}
            , where the largest number of records is concentrated. Gate overrides remain the only
            way to advance a held record, and each is attributed on the audit timeline.
          </p>
        </section>

        {records.length === 0 ? <EmptyState title="No Mega or Major records live" description="Records appear on the board once they enter the Assessment stage." data-id="element-1127" data-mp="i6pdx8s04huc" /> : <div className="grid gap-3 lg:grid-cols-3 2xl:grid-cols-6" data-id="element-1128" data-mp="rv7afdt902s6">
            {MEGA_MAJOR_STAGES.map(stage => {
          const column = records.filter(record => record.stage === stage);
          return <section key={stage} className="rounded-sm border border-border bg-card" aria-label={`${STAGE_META[stage].label} stage`} data-id="element-1129" data-mp="10xgmw982u687">
                  <header className="border-b border-border px-3 py-2.5" data-id="element-1130" data-mp="1pgs0mh05gc7n">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground" data-id="element-1131" data-mp="1biaget9gdhuh">
                      Stage {STAGE_META[stage].index}
                    </p>
                    <div className="mt-0.5 flex items-center justify-between gap-2" data-id="element-1132" data-mp="11r7fpskfr4vq">
                      <h2 className="text-[13px] font-semibold" data-id="element-1133" data-mp="16i5zx38jon49">{STAGE_META[stage].label}</h2>
                      <span className="font-mono text-[12px] text-muted-foreground" data-id="element-1134" data-mp="rtjhutrj9h9c">
                        {column.length}
                      </span>
                    </div>
                  </header>
                  <div className="space-y-2 p-2" data-id="element-1135" data-mp="it3le497jadz">
                    {column.length === 0 && <p className="px-1 py-4 text-center text-[11px] text-muted-foreground" data-id="element-1136" data-mp="nrojny4wfjzs">
                        No records
                      </p>}
                    {column.map(record => {
                const summary = summariseGate(record.gates[record.stage] ?? []);
                const sla = daysUntil(record.slaDueOn);
                return <Link key={record.id} to={`/lifecycle/${record.id}`} className="block rounded-sm border border-border bg-background p-2.5 transition-colors hover:border-uae-leaf" data-id="element-1137" data-mp="203skhtn6l2y">
                          <p className="text-[12px] font-medium leading-snug" data-id="element-1138" data-mp="3j7pc4mao43ek">
                            {record.eventName}
                          </p>
                          <p className="mt-0.5 truncate text-[11px] text-muted-foreground" data-id="element-1139" data-mp="3f7yi89puh75t">
                            {entityName(record.entityId)}
                          </p>
                          <p className="mt-1 truncate text-[11px] text-muted-foreground" data-id="element-1140" data-mp="1dgbwamkt82g7">
                            {record.hostBody}
                          </p>
                          <div className="mt-2 flex items-center justify-between gap-2" data-id="element-1141" data-mp="30bgt83ahufg7">
                            <ScoreChip score={record.aiScore} data-id="element-1142" data-mp="352fdfdylrxoq" />
                            <span className={cn('font-mono text-[11px]', summary.cleared ? 'text-uae-leaf' : 'text-muted-foreground')} data-id="element-1143" data-mp="3538jsdtngl6c">
                              gate {summary.percent}%
                            </span>
                          </div>
                          <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]" data-id="element-1144" data-mp="2lgked786itu5">
                            <span className="text-muted-foreground" data-id="element-1145" data-mp="2qb6daax2qtxc">
                              {record.daysInStage}d in stage
                            </span>
                            {sla < 0 && <span className="inline-flex items-center gap-1 text-uae-red" data-id="element-1146" data-mp="21qgccwaehokv">
                                <AlertTriangle className="h-3 w-3" aria-hidden="true" data-id="element-1147" data-mp="21sb0skkvrytp" />
                                overdue
                              </span>}
                          </div>
                        </Link>;
              })}
                  </div>
                </section>;
        })}
          </div>}
      </div>
    </div>;
}